import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormHelperText, makeStyles } from '@material-ui/core';
import StepParticipant from './stepParticipant';
import NotificationCard from '../notificationCard';
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrow_upward.svg';
import { ReactComponent as UsersIcon } from '../../../assets/icons/people_alt-icon.svg';
import { ASSIGNMENT_FIELDS } from '../../../constants/cycle';
import { isArrayEmpty, replaceObjectInList } from '../../../utility/helpers';
import { sortPeopleAlphabetically } from '../../../utility/uiUtils';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  label: {
    fontFamily: 'ProximaNova-Bold',
    fontSize: 18,
    lineHeight: '20px',
    marginBottom: spacing(6),
  },
  reviewsWrapper: {
    display: 'grid',
    gap: 36,
  },
  singleReview: {
    borderBottom: `1px solid ${primary.bluish6}`,
    paddingBottom: spacing(9),
  },
  assignmentsWrapper: {
    display: 'grid',
    gap: 4,
  },
  arrowDownWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    margin: spacing(1, 0),
  },
}));

const CycleStepAssignmentsField = ({
  translations,
  assignments,
  onToggleUserExclusion,
}) => {
  const classes = useStyles();

  const handleToggleExclusion =
    (assignmentIndex, userIndex, isReviewer = false) =>
    isExcluded => {
      const { REVIEWERS, CREATED_FOR } = ASSIGNMENT_FIELDS;

      const updatedUsers = replaceObjectInList(
        assignments[assignmentIndex][isReviewer ? REVIEWERS : CREATED_FOR],
        userIndex,
        {
          ...assignments[assignmentIndex][isReviewer ? REVIEWERS : CREATED_FOR][
            userIndex
          ],
          isExcluded,
        }
      );
      const sortedUsers = sortPeopleAlphabetically(
        updatedUsers,
        'isExcluded',
        true
      );

      onToggleUserExclusion(
        replaceObjectInList(assignments, assignmentIndex, {
          ...assignments[assignmentIndex],
          [isReviewer ? REVIEWERS : CREATED_FOR]: sortedUsers,
        })
      );
    };

  return (
    <div>
      {translations.label && (
        <FormHelperText className={classes.label}>
          {translations.label}
        </FormHelperText>
      )}
      <div className={classes.reviewsWrapper}>
        {!isArrayEmpty(assignments) ? (
          assignments.map(({ reviewers, created_for }, index) => {
            return (
              <div
                key={`participants_${index}`}
                className={classNames({
                  [classes.singleReview]: assignments.length > 1,
                })}
              >
                <div>
                  <div className={classes.assignmentsWrapper}>
                    {reviewers.map((reviewer, reviewerIndex) => {
                      const isDisabled =
                        !isArrayEmpty(created_for) &&
                        created_for.every(
                          reviewedUser => reviewedUser.isExcluded
                        );
                      return (
                        <StepParticipant
                          key={`step_participant_${reviewer.id}`}
                          translations={translations}
                          participant={reviewer}
                          isDisabled={isDisabled}
                          onToggleExclude={handleToggleExclusion(
                            index,
                            reviewerIndex,
                            true
                          )}
                        />
                      );
                    })}
                  </div>
                </div>
                {!isArrayEmpty(created_for) && (
                  <div>
                    <div className={classes.arrowDownWrapper}>
                      <ArrowDownIcon />
                    </div>
                    <div className={classes.assignmentsWrapper}>
                      {created_for.map((reviewedUser, reviewedUserIndex) => {
                        const isDisabled = reviewers.every(
                          reviewer => reviewer.isExcluded
                        );
                        return (
                          <StepParticipant
                            key={`step_participant_${reviewedUser.id}`}
                            translations={translations}
                            participant={reviewedUser}
                            isDisabled={isDisabled}
                            onToggleExclude={handleToggleExclusion(
                              index,
                              reviewedUserIndex
                            )}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <NotificationCard
            title={translations.noParticipants.title}
            customIcon={UsersIcon}
            hasIcon
          />
        )}
      </div>
    </div>
  );
};

CycleStepAssignmentsField.propTypes = {
  translations: PropTypes.object.isRequired,
  assignments: PropTypes.arrayOf(PropTypes.object).isRequired,
  onToggleUserExclusion: PropTypes.func.isRequired,
};

export default CycleStepAssignmentsField;
